/* eslint-disable @atlassian/relay/unused-fields */

import { fetchQuery, graphql } from 'react-relay';
import { type IEnvironment } from 'relay-runtime';

import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common/request';

import { type appConnectors_twg_microsite_SearchCombinedConfig_Query } from './__generated__/appConnectors_twg_microsite_SearchCombinedConfig_Query.graphql';

export type ConnectorInformation = {
	source: string;
	friendlyName: string;
	iconUrl: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	connectorCreatedAt: string | null;
	connectedAt: string | null;
	resourceType: string | null;
	providerId: string | null;
	integrationARI?: string | null;
};

export type KnowledgeSources = {
	knowledgeSources: ConnectorInformation[];
};

export const requestKnowledgeSources = (
	cloudId: string,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<KnowledgeSources>>;
} => {
	const { controller, execute } = requestWithAbort<KnowledgeSources>(
		'get',
		'/gateway/api/assist/rovo/v1/agents/configuration/knowledge',
		undefined,
		{
			credentials: 'include',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Product': 'rovo',
			'X-Experience-Id': 'ai-mate',
			'X-Cloudid': cloudId,
		},
	);
	return { controller, execute };
};

/**
 * Result of a `searchCombinedConfig` call.
 *
 * On success, `configuration` mirrors the GraphQL `search.configuration`
 * response. On failure (network error, no permission, missing
 * configuration), `success` is `false` and `error` may carry the
 * underlying exception for logging.
 */
export type SearchCombinedConfigResult =
	| {
			success: true;
			configuration: NonNullable<
				NonNullable<
					appConnectors_twg_microsite_SearchCombinedConfig_Query['response']['search']
				>['configuration']
			>;
	  }
	| {
			success: false;
			error?: unknown;
	  };

/**
 * Fetch combined first-party + third-party search configuration for the
 * current site via the `/gateway/api/graphql` endpoint.
 *
 * Uses Relay's imperative `fetchQuery` so the call participates in the
 * same network/cache layer wired up by `@atlassian/relay-environment-provider`.
 * Auth, tracing and `atl-attribution` headers are attached automatically by
 * that environment — no need to forward cookies/headers manually.
 */
export async function searchCombinedConfig(
	environment: IEnvironment,
	cloudId: string,
): Promise<SearchCombinedConfigResult> {
	try {
		const data = await fetchQuery<appConnectors_twg_microsite_SearchCombinedConfig_Query>(
			environment,
			graphql`
				query appConnectors_twg_microsite_SearchCombinedConfig_Query($cloudId: ID!) {
					search {
						configuration(cloudId: $cloudId) {
							firstPartyProducts {
								name
							}
							thirdPartyProducts {
								name
								outboundAuthUrl
								isUserOAuthed
								providerId
								integrationARI
								workspaceName
								workspaceUrl
								datasourceId
								connectionId
								connectorSyncType
								isImpersonationAuthEnabled
								connectorCreatedAt
								lightIcon
								entityTypes
								environmentType
							}
							intercomHmac
							siteMetadata {
								isRovoEnabled
								isJiraIssueIndexed
								applicationMode
								siteName
								orgId
								productEdition
								productInfo {
									name
									dataRegion
								}
							}
						}
					}
				}
			`,
			{ cloudId },
		).toPromise();

		const configuration = data?.search?.configuration;
		if (!configuration) {
			return { success: false };
		}
		return { success: true, configuration };
	} catch (error) {
		return { success: false, error };
	}
}
